/**
 * A one-of-N picker: a search box, a live count, and a radio group under it. The row is a render
 * prop — the list's behaviour (the three states, the reserve, the count, the radio group) is what
 * this component is, and the row is what its caller knows. The words are the caller's too, named by
 * one key prefix (`copy`) under which `search`, `searchPlaceholder`, `listLabel`, `loading`,
 * `error`, `retry`, `empty`, `noMatch` and the plural `count` stand.
 *
 * Promoted out of `features/assign-plan/ui` on 25 September 2026, on its second consumer: the
 * booking calendar's "Nowa sesja", which picks the client the same way.
 *
 * ── A radio group, because that is what "pick exactly one of N" IS ──────────────────────────
 * The browser gives roving focus, arrow-key navigation, `:checked` and the correct announcement for
 * free, and every one of those would have to be rebuilt badly on a list of buttons.
 *
 * ── `pending`, `error` and `empty` are three states, in a reserved box ──────────────────────
 * The list stands in a box of its own height from the first frame, so the answer landing changes
 * ink and never geometry, and the skeleton inside it is gated (150ms grace, 300ms minimum life).
 * A branch on `rows.length === 0` cannot tell a read still running from one that answered with
 * nothing, and the list would otherwise say "you have no clients" while loading.
 */

import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import choice from './choiceRow.module.css';
import skeleton from './skeleton.module.css';
import hiddenControl from './visuallyHiddenControl.module.css';

import styles from './PickerList.module.css';

interface PickerListProps<Row> {
  /** The i18n key prefix the list's words stand under — see the header. */
  readonly copy: string;
  readonly status: 'pending' | 'error' | 'ready';
  readonly rows: readonly Row[];
  readonly total: number;
  readonly query: string;
  readonly onQuery: (query: string) => void;
  readonly onRetry: () => void;
  readonly selectedId: number | null;
  readonly onSelect: (id: number) => void;
  readonly locked: boolean;
  readonly reasonId: string | null;
  /** Nothing is chosen and the form was refused for it: the search is marked invalid. */
  readonly invalid?: boolean;
  /**
   * The row's own identity on the wire — a `customerId`. Taken as a function rather than by
   * re-shaping every row into `{ id, … }`, which is a whole second array allocated on every
   * keystroke to rename one field.
   */
  readonly idOf: (row: Row) => number;
  readonly renderRow: (row: Row) => ReactNode;
}

/** How many skeleton bars stand in the reserve. Four rows is what the box holds at its own height. */
const SKELETON_ROWS = 4;

export function PickerList<Row>({
  copy,
  status,
  rows,
  total,
  query,
  onQuery,
  onRetry,
  selectedId,
  onSelect,
  locked,
  reasonId,
  invalid = false,
  idOf,
  renderRow,
}: PickerListProps<Row>) {
  const { t } = useTranslation();
  const searchId = useId();
  const groupName = useId();
  const gate = useSkeletonGate(status === 'pending');

  const message =
    status === 'pending'
      ? t(`${copy}.loading`)
      : status === 'error'
        ? t(`${copy}.error`)
        : total === 0
          ? t(`${copy}.empty`)
          : rows.length === 0
            ? t(`${copy}.noMatch`, { query })
            : t(`${copy}.count`, { count: rows.length });

  return (
    <>
      <input
        id={searchId}
        className={styles.search}
        type="search"
        autoComplete="off"
        value={query}
        aria-label={t(`${copy}.search`)}
        placeholder={t(`${copy}.searchPlaceholder`)}
        aria-disabled={locked ? 'true' : undefined}
        aria-invalid={invalid ? 'true' : undefined}
        aria-describedby={reasonId ?? undefined}
        onChange={(event) => {
          if (!locked) onQuery(event.target.value);
        }}
      />

      {/* Always mounted; the text is what changes. A `role="status"` inserted together with its
       * text is not reliably announced, and this one has to speak on every keystroke. */}
      <p className={styles.count} role="status">
        {message}
      </p>

      {/* The reserve. It holds its height in all four states, so nothing below it moves. */}
      <div
        className={styles.pickerBox}
        role="radiogroup"
        aria-label={t(`${copy}.listLabel`)}
        aria-describedby={reasonId ?? undefined}
      >
        {status === 'pending' &&
          gate.skeleton &&
          Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <span key={index} className={`${String(skeleton.bar)} ${String(styles.pickerBar)}`} />
          ))}

        {status === 'error' && (
          <button type="button" className={styles.retry} onClick={onRetry}>
            {t(`${copy}.retry`)}
          </button>
        )}

        {status === 'ready' && (
          <div className={`${String(choice.rows)} ${gate.reveal}`}>
            {rows.map((row) => {
              const id = idOf(row);
              const selected = id === selectedId;
              return (
                <label
                  key={id}
                  className={`${String(choice.row)} ${selected ? String(choice.rowSelected) : ''}`}
                >
                  <input
                    className={hiddenControl.control}
                    type="radio"
                    name={groupName}
                    value={id}
                    checked={selected}
                    aria-disabled={locked ? 'true' : undefined}
                    onChange={() => {
                      if (!locked) onSelect(id);
                    }}
                  />
                  <span className={choice.rowMark} aria-hidden="true" />
                  {renderRow(row)}
                </label>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
